import {Link, useLocation, useParams} from "react-router-dom";
import {Image} from "semantic-ui-react";
import React, {useEffect, useMemo} from "react";
import {getChampionships} from "../../../data/data";
import styled from "styled-components";
import {useFilters, useGlobalFilter, usePagination, useSortBy, useTable} from "react-table";
import {FormProvider, useForm} from "react-hook-form";
import {device} from "../../../theme";
import ChampionshipTable from "./ChampionshipTable";
import {useSearchParams} from "../../hooks/useSearchParams";
import SelectStyled from "../../inputs/SelectStyled";
import Select from "react-select";
import InputWithLabel from "../../inputs/InputWithLabel";
import {Menu} from "../../layout/Menu";

const championships = getChampionships();

export const ChampionshipClub = () => {
    const methods = useForm();
    const {setParam, queryParams} = useSearchParams();
    const location = useLocation();
    const params = useParams();
    const id = params.id;
    const filterFields = useMemo(() => ["club"], [])
    const championship = useMemo(() => championships.find(c => c.id === id), [id]);

    useEffect(() => {
        resetAllFilters();

        queryParams.forEach((value, key) => {
            if (key === "search") {
                setGlobalFilter(value);
                methods.setValue("search", value);
                return;
            }
            if (!filterFields.includes(key)) return;
            setFilter(key, value || undefined);
        });
    }, [location]);

    function resetAllFilters() {
        setAllFilters([]);
    }

    const data = useMemo(() => {
        if (!championship) return [];
        const result = [];
        for (const part of championship?.parts) {
            for (const club of part.clubs) {
                let p = result.find(r => r.club === club.club);
                if (!p) {
                    p = {club: club.club, sum: 0, scoreDistribution: {}}
                    if ("nr" in club)
                        p.nr = club.nr;
                    result.push(p);
                }
                p[part.part] = club.score;
                p.sum += club.score;
                if (club.scoreDistribution) {
                    for (const [score, count] of Object.entries(club.scoreDistribution)) {
                        p.scoreDistribution[score] = (p.scoreDistribution[score] || 0) + count;
                    }
                }
            }
        }

        // Compare score distributions for tiebreaker (highest single score wins, then count, then next score)
        function compareDistribution(a, b) {
            const aKeys = Object.keys(a.scoreDistribution).map(Number).sort((x, y) => y - x);
            const bKeys = Object.keys(b.scoreDistribution).map(Number).sort((x, y) => y - x);
            const allScores = [...new Set([...aKeys, ...bKeys])].sort((x, y) => y - x);
            for (const score of allScores) {
                const aCount = a.scoreDistribution[score] || 0;
                const bCount = b.scoreDistribution[score] || 0;
                if (aCount !== bCount) return bCount - aCount;
            }
            return 0;
        }

        const sorted = result.sort((a, b) => {
            if (b.sum !== a.sum) return b.sum - a.sum;
            return compareDistribution(a, b);
        });

        let pos = 1;
        let count = 0;
        let prevScore = 0;
        let prevClub = null;
        sorted.forEach(r => {
            count++;
            if ("nr" in r) {
                r.pos = r.nr;
            } else {
                if (r.sum !== prevScore || (prevClub && compareDistribution(r, prevClub) !== 0))
                    pos = count;
                r.pos = pos;
            }
            prevScore = r.sum;
            prevClub = r;
            // Mark tiebreaker winner with *
            if (r.sum === prevScore && pos < count) {
                // This club lost tiebreaker, already has correct pos
            }
        });

        return sorted;
    }, [championship])

    const columns = useMemo(() => {
            const cols = [
                {
                    Header: "Plass",
                    accessor: "pos",
                    disableFilters: true
                },
                {
                    Header: "Klubb",
                    accessor: "club",
                    Filter: SelectColumnFilter,
                    filter: "exact"
                },
            ]
            if (data.length > 0) {
                for (const part of Object.keys(data[0])) {
                    if (part==="") continue;
                    if (["club", "sum", "pos", "nr", "scoreDistribution"].includes(part)) continue;
                    cols.push({
                        Header: part,
                        accessor: part,
                        disableFilters: true
                    })
                }
                cols.push({
                    Header: "Sum",
                    accessor: "sum",
                    disableFilters: true
                })
            }
            return cols;
        }
        , [data])

    const search = methods.watch("search");

    const initialState = useMemo(() => {
        const filtersFromUrl = [...queryParams.entries()]
            .filter((entry) => filterFields.includes[entry[0]])
            .map((entry) => ({id: entry[0], value: entry[1] || undefined}));
        return {
            pageSize: 10,
            sortBy: [
                {
                    id: 'sum',
                    desc: true
                },
                {
                    id: 'pos',
                    asc: true
                }
            ]
        }
    }, []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        rows,

        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        gotoPage,
        pageOptions,
        pageCount,

        setFilter,
        setAllFilters,
        setGlobalFilter,
        state: {pageIndex},
    } = useTable(
        {columns, data, initialState},
        useGlobalFilter,
        useFilters,
        useSortBy,
        usePagination
    );

    function SelectColumnFilter({column: {filterValue, preFilteredRows, id}}) {
        const options = useMemo(() => {
            const values = [...new Set(preFilteredRows.map((row) => row.values[id]))];
            let options;

            options = values
                .filter((item) => !!item)
                ?.map((item) => ({
                    value: item,
                    label: item,
                }))
                .sort((a, b) => -a.label?.localeCompare(b.label));
            return [{label: "Alle", value: ""}, ...options];
        }, [id, preFilteredRows]);
        const currentValue = options.find((item) => item.value === filterValue);

        return (
            <SelectStyled
                style={{minWidth: "12rem", marginTop: "0.2rem", marginBottom: "0.2rem"}}
            >
                <Select
                    defaultValue={options[0]}
                    value={currentValue || undefined}
                    onChange={(e) => setParam(id, e.value)}
                    options={options}
                    classNamePrefix="rs"
                    name={id}
                    menuPortalTarget={document.body}
                    styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                />
            </SelectStyled>
        );
    }

    useEffect(() => {
        if (search !== undefined) {
            setGlobalFilter(search)
            setParam("search", search)
        }
    }, [search]);

    return <>
        <Header>
            <div>
                <a href="https://nsff.no"><Image src={"/resultater/images/logo-darkgreen.png"} alt="Logo"
                                                 height={"60"}/></a>

            </div>
            <div>
                <Link to={`/`}>
                    {championship?.name}
                </Link>
            </div>
            <div>
                <Link to={"/mesterskap"}>Tilbake</Link>
            </div>
        </Header>
        <Menu/>
        {championship &&
            <MainArea>
                <Container>
                    <FormProvider {...methods}>
                        <SearchInput
                            name="search"
                            label={"Søk i navn"}
                            autoComplete="off"
                        />
                        <ChampionshipTable
                            getTableProps={getTableProps}
                            headerGroups={headerGroups}
                            getTableBodyProps={getTableBodyProps}
                            page={page}
                            prepareRow={prepareRow}
                            pageCount={pageCount}
                            previousPage={previousPage}
                            canPreviousPage={canPreviousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                            isLoading={false}
                            rows={rows}
                        />
                    </FormProvider>
                </Container>
            </MainArea>
        }
    </>
}

const Container = styled.div`
  opacity: 0;
  animation: fadeIn 300ms ease forwards;
`;

const MainArea = styled.div`
  padding: 1.25rem 2.5rem 3.125rem 3.125rem;

  @media ${device.mobile} {
    padding: 1.25rem 0.625rem 3.125rem 0.625rem;
  }
`;

const SearchInput = styled(InputWithLabel)`
  flex-grow: 1;
  max-width: 27rem;
  margin-right: 1rem;
`;

const Header = styled.div`
  height: 70px;
  background-color: #90b361;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #36501c;
  font-family: 'Fibon Neue';

  > div {
    display: flex;
    flex-direction: row;
    margin-left: 10px;

    > div {
      margin-left: 20px;
    }

    > a {
      color: ${(p) => p.theme.color.primary.base};
    }

    align-items: center;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    cursor: pointer;
    font-size: 1.4rem;
  }

  > div:nth-child(3) {
    margin-right: 20px;

    > a {
      color: ${(p) => p.theme.color.primary.base};
    }
  }
`